/* ============================================================
   GIST LIBRARY — THE GIST READER  (study layer, gxrd-*)
   A premium active-reading workspace over the article page.
   Dependency-free · idempotent · reduced-motion + a11y aware ·
   progressive enhancement (no JS => the article reads as before).

   Modules: TOC + scroll-spy · reading progress · settings
   (font / line-height / mode, persisted) · offset-based
   highlighter + export · browser TTS ("Listen") · live, MEASURED
   reading stats (no fabricated numbers) · resume · bookmark ·
   floating dock + accessible off-canvas drawers.

   Security: this file sets NO innerHTML. Every node is built with
   createElement + textContent; all icons are server-rendered SVG.
   Teardown: each init registers document/window listeners through
   an AbortController + a tracked interval, both released on
   'shopify:section:unload' so the Theme Editor never leaks ghosts.
   ============================================================ */
(function () {
  'use strict';
  if (window.__gxrdLoaded) return;
  window.__gxrdLoaded = true;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function isModal() { return window.matchMedia('(max-width: 1199px)').matches; }

  /* ---------- tiny helpers ---------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function el(tag, cls) { var n = document.createElement(tag); if (cls) n.className = cls; return n; }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  var store = {
    get: function (k, fb) { try { var v = localStorage.getItem(k); return v == null ? fb : JSON.parse(v); } catch (e) { return fb; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
    del: function (k) { try { localStorage.removeItem(k); } catch (e) {} }
  };

  function rafThrottle(fn) {
    var ticking = false, lastArgs;
    return function () { lastArgs = arguments; if (ticking) return; ticking = true;
      requestAnimationFrame(function () { ticking = false; fn.apply(null, lastArgs); }); };
  }
  function relTime(ts) {
    var s = Math.round((Date.now() - ts) / 1000);
    if (s < 45) return 'just now';
    var m = Math.round(s / 60); if (m < 60) return m + ' min ago';
    var h = Math.round(m / 60); if (h < 24) return h + (h === 1 ? ' hr ago' : ' hrs ago');
    var d = Math.round(h / 24); return d + (d === 1 ? ' day ago' : ' days ago');
  }

  /* ============================================================ */
  function init(root) {
    if (!root || root.__gxrd) return; root.__gxrd = true;

    var body = $('.gxr-article__body', root);
    if (!body) return;                          /* not the reading view */
    var key = (root.getAttribute('data-article') || location.pathname).trim();
    var audioLink = root.getAttribute('data-audio-link') || '/collections/audio-summaries';
    var docTitle = (root.getAttribute('data-title') || document.title).trim();
    var leftRail = $('.gxrd__rail--left', root);
    var rightRail = $('.gxrd__rail--right', root);

    /* teardown plumbing — every doc/window listener flows through SIG */
    var ac = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var SIG = ac ? ac.signal : undefined;
    var timerId = 0;
    function on(target, type, fn, opts) { opts = opts || {}; if (SIG) opts.signal = SIG; target.addEventListener(type, fn, opts); }

    var TOTAL_WORDS = (body.textContent.trim().match(/\S+/g) || []).length;

    /* =========================================================
       1) HEADINGS → ids + TABLE OF CONTENTS + cached scroll-spy
       ========================================================= */
    var heads = $all('h2, h3', body).filter(function (h) { return h.textContent.trim(); });
    var tocLinks = [], slug = {};
    heads.forEach(function (h, i) {
      if (!h.id) {
        var base = (h.textContent.trim().toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 40)) || ('sec-' + i);
        var id = base, n = 2; while (slug[id] || document.getElementById(id)) { id = base + '-' + (n++); }
        slug[id] = 1; h.id = id;
      }
    });

    var tocWrap = $('[data-gxrd-toc]', root);
    if (tocWrap && heads.length) {
      var ul = el('ul', 'gxrd-toc'); ul.setAttribute('role', 'list');
      heads.forEach(function (h) {
        var li = el('li');
        var b = el('button', 'gxrd-toc__link' + (h.tagName === 'H3' ? ' gxrd-toc__link--h3' : ''));
        b.type = 'button'; b.textContent = h.textContent.trim(); b.setAttribute('data-target', h.id);
        b.addEventListener('click', function () { jumpTo(h); closeDrawers(); });
        li.appendChild(b); ul.appendChild(li); tocLinks.push({ link: b, head: h });
      });
      tocWrap.appendChild(ul);
    } else if (tocWrap) {
      var tcard = tocWrap.closest('.gxrd-card'); if (tcard) tcard.style.display = 'none';
    }
    function jumpTo(h) {
      h.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' });
      h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) {}
    }
    var secLabel = $('[data-gxrd-sec]', root), activeIdx = -1, headTops = [];
    function measureHeads() { headTops = heads.map(function (h) { return h.getBoundingClientRect().top + window.scrollY; }); }
    measureHeads();
    function spy() {
      if (!heads.length) return;
      var cur = 0, mark = window.scrollY + window.innerHeight * 0.28;
      for (var i = 0; i < headTops.length; i++) { if (headTops[i] <= mark) cur = i; else break; }
      if (cur !== activeIdx) {
        activeIdx = cur;
        tocLinks.forEach(function (t, i) { t.link.setAttribute('aria-current', i === cur ? 'true' : 'false'); });
        if (secLabel) secLabel.textContent = heads[cur].textContent.trim();
      }
    }

    /* =========================================================
       2) READING PROGRESS  → top bar, ring, %, stats
       ========================================================= */
    var bar = $('.gxrd__progress', root);
    var ringFill = $('[data-gxrd-ringfill]', root), ringPct = $('[data-gxrd-ringpct]', root), dockPct = $('[data-gxrd-dockpct]', root);
    var ringLen = 0;
    if (ringFill) { ringLen = ringFill.getTotalLength ? ringFill.getTotalLength() : 163; ringFill.style.strokeDasharray = ringLen; }
    var frac = 0;
    function computeFrac() {
      var r = body.getBoundingClientRect(), top = r.top + window.scrollY, h = r.height;
      if (h <= 0) return 0;
      /* progress = how far a reading line at 62% viewport height has travelled
         through the article. 0 at load (line above the article), 1 once the line
         passes the last word — honest for very short AND very long posts. */
      var line = window.scrollY + window.innerHeight * 0.62;
      return clamp((line - top) / h, 0, 1);
    }
    function paintProgress() {
      frac = computeFrac();
      var pct = Math.round(frac * 100);
      if (bar) bar.style.setProperty('--gxrd-p', pct + '%');
      if (ringFill) ringFill.style.strokeDashoffset = ringLen * (1 - frac);
      if (ringPct) ringPct.textContent = pct + '%';
      if (dockPct) dockPct.textContent = pct + '%';
      updateStats(); saveResume();
    }

    /* =========================================================
       3) SETTINGS — font-size · line-height · reading mode
       ========================================================= */
    var DEF = { font: 18.5, lh: 1.75, mode: 'paper' };
    var PREFS = store.get('gxrd:prefs', DEF);
    function applyPrefs() {
      root.style.setProperty('--gxrd-font', PREFS.font + 'px');
      root.style.setProperty('--gxrd-lh', PREFS.lh);
      root.classList.remove('gxrd--sepia', 'gxrd--night');
      if (PREFS.mode === 'sepia') root.classList.add('gxrd--sepia');
      if (PREFS.mode === 'night') root.classList.add('gxrd--night');
      measureHeads();                 /* headings shifted — refresh the spy cache */
    }
    applyPrefs();
    var fontIn = $('[data-gxrd-font]', root), fontOut = $('[data-gxrd-fontout]', root);
    var lhIn = $('[data-gxrd-lh]', root), lhOut = $('[data-gxrd-lhout]', root);
    function syncSettingsUI() {
      if (fontIn) { fontIn.value = PREFS.font; if (fontOut) fontOut.textContent = PREFS.font + 'px'; }
      if (lhIn) { lhIn.value = PREFS.lh; if (lhOut) lhOut.textContent = (+PREFS.lh).toFixed(2); }
      $all('[data-gxrd-mode]', root).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-gxrd-mode') === PREFS.mode ? 'true' : 'false'); });
    }
    syncSettingsUI();
    if (fontIn) fontIn.addEventListener('input', function () { PREFS.font = +fontIn.value; if (fontOut) fontOut.textContent = PREFS.font + 'px'; applyPrefs(); store.set('gxrd:prefs', PREFS); });
    if (lhIn) lhIn.addEventListener('input', function () { PREFS.lh = +lhIn.value; if (lhOut) lhOut.textContent = (+PREFS.lh).toFixed(2); applyPrefs(); store.set('gxrd:prefs', PREFS); });
    $all('[data-gxrd-mode]', root).forEach(function (btn) {
      btn.addEventListener('click', function () { PREFS.mode = btn.getAttribute('data-gxrd-mode'); applyPrefs(); store.set('gxrd:prefs', PREFS); syncSettingsUI(); });
    });
    var resetBtn = $('[data-gxrd-reset]', root);
    if (resetBtn) resetBtn.addEventListener('click', function () { PREFS = { font: DEF.font, lh: DEF.lh, mode: DEF.mode }; store.set('gxrd:prefs', PREFS); applyPrefs(); syncSettingsUI(); toast('Reading settings reset'); });

    /* =========================================================
       4) HIGHLIGHTER — offset model + persistence + export
       ========================================================= */
    var HLS = store.get('gxrd:hl:' + key, []);
    var curInk = 'amber';
    var hlListWrap = $('[data-gxrd-hllist]', root), hlEmpty = $('[data-gxrd-hlempty]', root), hlCount = $('[data-gxrd-hlcount]', root);

    function textNodes() { var out = [], w = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, null), n; while ((n = w.nextNode())) { if (n.nodeValue.length) out.push(n); } return out; }
    /* Symmetric encoder: the SAME coordinate system applyRange decodes
       (cumulative sum of text-node lengths), so writes and reads never drift. */
    function offsetOf(container, off) {
      if (container.nodeType === 3) {
        var nodes = textNodes(), total = 0;
        for (var i = 0; i < nodes.length; i++) { if (nodes[i] === container) return total + Math.min(off, nodes[i].nodeValue.length); total += nodes[i].nodeValue.length; }
        return total;
      }
      var r = document.createRange(); r.setStart(body, 0);
      try { r.setEnd(container, off); return r.toString().length; } catch (e) { return -1; }
    }
    function overlapsExisting(s, e) { for (var i = 0; i < HLS.length; i++) { if (s < HLS[i].end && e > HLS[i].start) return true; } return false; }
    function applyRange(start, end, color, id) {
      if (end <= start) return false;
      var nodes = textNodes(), pos = 0, jobs = [];
      for (var i = 0; i < nodes.length; i++) {
        var len = nodes[i].nodeValue.length, ns = pos, ne = pos + len;
        if (ne > start && ns < end) { var ls = Math.max(start, ns) - ns, le = Math.min(end, ne) - ns; if (le > ls) jobs.push({ node: nodes[i], ls: ls, le: le }); }
        pos = ne; if (ns >= end) break;
      }
      var ok = false;
      jobs.forEach(function (j) {
        try {
          var r = document.createRange(); r.setStart(j.node, j.ls); r.setEnd(j.node, j.le);
          var m = el('mark', 'gxrd-hl gxrd-hl--' + color); m.setAttribute('data-hl-id', id); m.setAttribute('title', 'Click to remove highlight');
          r.surroundContents(m); ok = true;
        } catch (e) {}
      });
      return ok;
    }
    function renderHls() {
      if (hlEmpty) hlEmpty.style.display = HLS.length ? 'none' : 'block';
      if (hlCount) hlCount.textContent = HLS.length;
      if (hlListWrap) {
        while (hlListWrap.firstChild) hlListWrap.removeChild(hlListWrap.firstChild);
        HLS.slice().sort(function (a, b) { return a.start - b.start; }).forEach(function (h) {
          var li = el('li', 'gxrd-hls__item'); li.setAttribute('data-c', h.color);
          var j = el('button', 'gxrd-hls__jump'); j.type = 'button';
          var q = el('span', 'gxrd-hls__q'); q.textContent = '“' + (h.text.length > 120 ? h.text.slice(0, 117) + '…' : h.text) + '”';
          var t = el('span', 'gxrd-hls__t'); t.textContent = relTime(h.ts);
          j.appendChild(q); j.appendChild(t);
          j.addEventListener('click', function () { flashHl(h.id); closeDrawers(); });
          var d = el('button', 'gxrd-hls__del'); d.type = 'button'; d.setAttribute('aria-label', 'Remove highlight'); d.textContent = '✕';
          d.addEventListener('click', function () { removeHl(h.id); });
          li.appendChild(j); li.appendChild(d); hlListWrap.appendChild(li);
        });
      }
      updateStats();
    }
    function addHl(start, end, text, color) {
      var id = 'h' + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36);
      if (!applyRange(start, end, color, id)) return false;
      HLS.push({ id: id, start: start, end: end, text: text, color: color, ts: Date.now() });
      store.set('gxrd:hl:' + key, HLS); renderHls(); return true;
    }
    function removeHl(id) {
      var parents = [];
      $all('mark[data-hl-id="' + id + '"]', body).forEach(function (m) {
        var p = m.parentNode; while (m.firstChild) p.insertBefore(m.firstChild, m); p.removeChild(m);
        if (parents.indexOf(p) === -1) parents.push(p);
      });
      parents.forEach(function (p) { p.normalize(); });
      HLS = HLS.filter(function (h) { return h.id !== id; });
      store.set('gxrd:hl:' + key, HLS); renderHls();
    }
    function flashHl(id) {
      var marks = $all('mark[data-hl-id="' + id + '"]', body); if (!marks.length) return;
      marks[0].scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'center' });
      marks.forEach(function (m) { m.classList.add('gxrd-hl--flash'); setTimeout(function () { m.classList.remove('gxrd-hl--flash'); }, 1500); });
    }

    /* selection toolbar (built in JS; no innerHTML) */
    var sel = $('.gxrd-sel', root), INKS = ['amber', 'teal', 'blue', 'violet'];
    if (sel) {
      INKS.forEach(function (c) {
        var b = el('button', 'gxrd-sel__ink gxrd-ink--' + c); b.type = 'button'; b.setAttribute('aria-label', 'Highlight in ' + c);
        b.addEventListener('mousedown', function (ev) { ev.preventDefault(); });
        b.addEventListener('click', function () { commitSelection(c); });
        sel.appendChild(b);
      });
      var xb = el('button', 'gxrd-sel__x'); xb.type = 'button'; xb.setAttribute('aria-label', 'Dismiss'); xb.textContent = '✕';
      xb.addEventListener('mousedown', function (ev) { ev.preventDefault(); });
      xb.addEventListener('click', hideSel); sel.appendChild(xb);
    }
    function showSelFor(range) {
      if (!sel) return; var rects = range.getClientRects(); if (!rects.length) return;
      var r = rects[0], host = root.getBoundingClientRect();
      sel.classList.add('is-on');                     /* show first so offsetWidth measures */
      var halfW = (sel.offsetWidth || 160) / 2, PAD = 10;
      var centerX = r.left - host.left + r.width / 2;
      var clampedX = clamp(centerX, halfW + PAD, Math.max(halfW + PAD, host.width - halfW - PAD));
      sel.style.left = clampedX + 'px';
      sel.style.setProperty('--gxrd-sel-arrow', clamp(centerX - (clampedX - halfW), 14, halfW * 2 - 14) + 'px');
      if (r.top < 64) { sel.style.top = (r.bottom - host.top + 14) + 'px'; sel.setAttribute('data-below', '1'); }
      else { sel.style.top = (r.top - host.top - 8) + 'px'; sel.removeAttribute('data-below'); }
    }
    function hideSel() { if (sel) sel.classList.remove('is-on'); }
    function commitSelection(color) {
      var s = window.getSelection(); if (!s || s.isCollapsed || !s.rangeCount) return;
      var range = s.getRangeAt(0);
      if (!body.contains(range.startContainer) || !body.contains(range.endContainer)) return;
      var start = offsetOf(range.startContainer, range.startOffset), end = offsetOf(range.endContainer, range.endOffset);
      if (start < 0 || end < 0) return; if (end < start) { var t = start; start = end; end = t; }
      var text = range.toString().replace(/\s+/g, ' ').trim();
      if (end - start < 1 || !text) return;
      if (overlapsExisting(start, end)) { toast('That overlaps a highlight you already made'); s.removeAllRanges(); hideSel(); return; }
      addHl(start, end, text, color); s.removeAllRanges(); hideSel();
    }
    function onSelectionMaybe() {
      var s = window.getSelection();
      if (!s || s.isCollapsed || !s.rangeCount) { hideSel(); return; }
      var range = s.getRangeAt(0);
      if (!body.contains(range.commonAncestorContainer)) { hideSel(); return; }
      if (range.toString().trim().length < 2) { hideSel(); return; }
      showSelFor(range);
    }
    body.addEventListener('mouseup', function () { setTimeout(onSelectionMaybe, 1); });
    body.addEventListener('touchend', function () { setTimeout(onSelectionMaybe, 1); });
    body.addEventListener('keyup', function (e) { if (e.shiftKey || e.key === 'Shift' || (e.key && e.key.indexOf('Arrow') === 0)) setTimeout(onSelectionMaybe, 1); });
    on(document, 'selectionchange', function () { if (sel && sel.classList.contains('is-on')) { var s = window.getSelection(); if (!s || s.isCollapsed) hideSel(); } });
    on(document, 'mousedown', function (e) { if (sel && !sel.contains(e.target) && !body.contains(e.target)) hideSel(); });
    body.addEventListener('click', function (e) {
      var m = e.target.closest && e.target.closest('.gxrd-hl'); if (!m) return;
      var s = window.getSelection(); if (s && !s.isCollapsed) return;
      removeHl(m.getAttribute('data-hl-id'));
    });
    $all('[data-gxrd-ink]', root).forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-gxrd-ink') === curInk ? 'true' : 'false');
      b.addEventListener('click', function () {
        curInk = b.getAttribute('data-gxrd-ink');
        $all('[data-gxrd-ink]', root).forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        toast('Select any text to highlight');
      });
    });

    /* export + print */
    function buildMarkdown() {
      var lines = ['# ' + docTitle, '', location.href, ''];
      if (HLS.length) {
        lines.push('## Highlights', '');
        HLS.slice().sort(function (a, b) { return a.start - b.start; }).forEach(function (h) { lines.push('> ' + h.text, '', '— ' + h.color + ' · ' + new Date(h.ts).toLocaleString(), ''); });
      } else { lines.push('_No highlights yet._', ''); }
      lines.push('---', 'Saved from Gist Library · The Gist Journal');
      return lines.join('\n');
    }
    var copyBtn = $('[data-gxrd-copy]', root), dlBtn = $('[data-gxrd-download]', root), printBtn = $('[data-gxrd-print]', root);
    if (copyBtn) copyBtn.addEventListener('click', function () {
      var md = buildMarkdown();
      function fallbackCopy() { var ta = el('textarea'); ta.value = md; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); toast('Notes copied'); } catch (e) { toast('Copy not supported'); } document.body.removeChild(ta); }
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(md).then(function () { toast('Notes copied to clipboard'); }, fallbackCopy); else fallbackCopy();
    });
    if (dlBtn) dlBtn.addEventListener('click', function () {
      var blob = new Blob([buildMarkdown()], { type: 'text/markdown;charset=utf-8' });
      var a = el('a'); a.href = URL.createObjectURL(blob); a.download = (key || 'article').replace(/[^\w-]/g, '') + '-notes.md';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(function () { URL.revokeObjectURL(a.href); }, 4000); toast('Notes downloaded');
    });
    if (printBtn) printBtn.addEventListener('click', function () { window.print(); });

    /* =========================================================
       5) LISTEN — browser text-to-speech (honest, client-side)
       ========================================================= */
    var ttsWrap = $('[data-gxrd-tts]', root), synth = window.speechSynthesis;
    var ttsBlocks = $all('p, h2, h3, blockquote, li', body).filter(function (b) { return b.textContent.trim().length > 1; });
    var playBtn = $('[data-gxrd-play]', root), ttsState = $('[data-gxrd-ttsstate]', root), ttsCount = $('[data-gxrd-ttscount]', root);
    var speedIn = $('[data-gxrd-speed]', root), speedOut = $('[data-gxrd-speedout]', root), dockPlay = $('[data-gxrd-dockplay]', root);
    var rate = store.get('gxrd:rate', 1), ttsIdx = 0, speaking = false, paused = false, voice = null, watchdog = 0;

    if (!synth || !ttsBlocks.length) {
      if (ttsWrap) { var live = $('[data-gxrd-ttslive]', ttsWrap); if (live) live.style.display = 'none'; }
      if (dockPlay) dockPlay.style.display = 'none';
    } else {
      var pickVoice = function () {
        var vs = synth.getVoices() || [];
        voice = vs.filter(function (v) { return /^en(-|_|$)/i.test(v.lang); })
          .sort(function (a, b) { return (/(natural|neural|google|samantha|aria)/i.test(b.name) ? 1 : 0) - (/(natural|neural|google|samantha|aria)/i.test(a.name) ? 1 : 0); })[0] || vs[0] || null;
      };
      pickVoice(); if (synth.onvoiceschanged !== undefined) synth.onvoiceschanged = pickVoice;
      var setPlayUI = function (o) {
        if (playBtn) { playBtn.setAttribute('data-playing', o ? 'true' : 'false'); playBtn.setAttribute('aria-label', o ? 'Pause reading' : 'Read this article aloud'); }
        if (dockPlay) { dockPlay.setAttribute('data-playing', o ? 'true' : 'false'); dockPlay.setAttribute('aria-pressed', o ? 'true' : 'false'); }
      };
      var setState = function (t) { if (ttsState) ttsState.textContent = t; };       /* live region — transitions only */
      var setCount = function (t) { if (ttsCount) ttsCount.textContent = t; };        /* non-live — per-block churn */
      var clearSpeak = function () { $all('.gxrd-speaking', body).forEach(function (n) { n.classList.remove('gxrd-speaking'); }); };
      if (speedIn) { speedIn.value = rate; if (speedOut) speedOut.textContent = (+rate).toFixed(1) + '×'; speedIn.addEventListener('input', function () { rate = +speedIn.value; if (speedOut) speedOut.textContent = rate.toFixed(1) + '×'; store.set('gxrd:rate', rate); if (speaking && !paused) speakFrom(ttsIdx); }); }
      function speakFrom(i) {
        clearTimeout(watchdog);
        if (i >= ttsBlocks.length) { stopTTS(); setState('Finished. Nicely done.'); setCount(''); return; }
        ttsIdx = i; clearSpeak();
        var node = ttsBlocks[i]; node.classList.add('gxrd-speaking');
        if (!REDUCE) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (!voice) pickVoice();
        try { synth.cancel(); } catch (e) {}
        var u = new SpeechSynthesisUtterance(node.textContent.trim());
        if (voice) u.voice = voice; u.rate = clamp(rate, 0.5, 2); u.lang = (voice && voice.lang) || 'en-US';
        u.onend = function () { if (speaking && !paused) speakFrom(ttsIdx + 1); };
        u.onerror = function () { if (speaking && !paused) speakFrom(ttsIdx + 1); };
        setCount((i + 1) + ' / ' + ttsBlocks.length);
        try { synth.speak(u); } catch (e) {}
      }
      function startTTS() { speaking = true; paused = false; setPlayUI(true); setState('Reading aloud — by your browser.');
        var from = 0; for (var i = 0; i < ttsBlocks.length; i++) { if (ttsBlocks[i].getBoundingClientRect().top > 70) { from = i; break; } from = i; }
        speakFrom(from);
      }
      function stopTTS() { speaking = false; paused = false; clearTimeout(watchdog); try { synth.cancel(); } catch (e) {} clearSpeak(); setPlayUI(false); }
      function toggleTTS() {
        if (!speaking) { startTTS(); return; }
        if (paused) {
          paused = false; try { synth.resume(); } catch (e) {} setPlayUI(true); setState('Reading aloud — resumed.');
          /* Chrome stalls long utterances on resume: if nothing speaks shortly, re-issue */
          clearTimeout(watchdog); watchdog = setTimeout(function () { if (speaking && !paused && synth && !synth.speaking) speakFrom(ttsIdx); }, 1400);
        } else { paused = true; try { synth.pause(); } catch (e) {} setPlayUI(false); setState('Paused — tap play to resume.'); }
      }
      if (playBtn) playBtn.addEventListener('click', toggleTTS);
      if (dockPlay) dockPlay.addEventListener('click', toggleTTS);
      var stopBtn = $('[data-gxrd-stop]', root); if (stopBtn) stopBtn.addEventListener('click', function () { stopTTS(); setState('Stopped. Play resumes from the top of your screen.'); setCount(''); });
      on(window, 'pagehide', stopTTS);
      on(window, 'beforeunload', function () { try { synth.cancel(); } catch (e) {} });
    }

    /* =========================================================
       6) LIVE STATS — all MEASURED (no fabricated numbers)
       ========================================================= */
    var stWords = $('[data-gxrd-st-words]', root), stTime = $('[data-gxrd-st-time]', root), stWpm = $('[data-gxrd-st-wpm]', root),
        stPct = $('[data-gxrd-st-pct]', root), stLeft = $('[data-gxrd-st-left]', root);
    var activeMs = 0, lastTick = Date.now(), visible = !document.hidden;
    if (stWords) stWords.textContent = TOTAL_WORDS.toLocaleString();
    function fmtTime(ms) { var s = Math.round(ms / 1000); if (s < 60) return s + 's'; var m = Math.floor(s / 60); return m + 'm ' + (s % 60) + 's'; }
    function updateStats() {
      if (stTime) stTime.textContent = fmtTime(activeMs);
      if (stPct) stPct.textContent = Math.round(frac * 100) + '%';
      var min = activeMs / 60000, wordsRead = Math.round(frac * TOTAL_WORDS);
      var wpm = (min > 0.33 && wordsRead > 20) ? Math.round(wordsRead / min) : null;
      if (stWpm) stWpm.textContent = wpm ? wpm.toLocaleString() : '—';
      if (stLeft) { var remaining = Math.max(0, TOTAL_WORDS - wordsRead), pace = wpm || 230, mLeft = Math.ceil(remaining / pace); stLeft.textContent = frac >= 0.985 ? 'You have reached the end.' : ('about ' + mLeft + ' min left at your pace.'); }
    }
    on(document, 'visibilitychange', function () { visible = !document.hidden; lastTick = Date.now(); });
    timerId = setInterval(function () { var now = Date.now(); if (visible && document.hasFocus()) activeMs += now - lastTick; lastTick = now; updateStats(); }, 1000);

    /* =========================================================
       7) RESUME — remember scroll position per article
       ========================================================= */
    var resumeEl = $('.gxrd-resume', root);
    var saveResume = (function () { var t = 0; return function () { var now = Date.now(); if (now - t < 1200) return; t = now; if (frac > 0.02 && frac < 0.97) store.set('gxrd:pos:' + key, frac); else if (frac >= 0.97) store.del('gxrd:pos:' + key); }; })();
    (function offerResume() {
      var saved = store.get('gxrd:pos:' + key, 0);
      if (!resumeEl || !saved || saved < 0.06 || saved > 0.9) return;
      var go = $('.gxrd-resume__go', resumeEl), x = $('.gxrd-resume__x', resumeEl), pctEl = $('[data-gxrd-resumepct]', resumeEl);
      if (pctEl) pctEl.textContent = Math.round(saved * 100) + '%';
      var hideT = setTimeout(hide, 9000);
      function hide() { resumeEl.classList.remove('is-on'); clearTimeout(hideT); }
      if (go) go.addEventListener('click', function () {
        var r = body.getBoundingClientRect(), top = r.top + window.scrollY;
        window.scrollTo({ top: Math.max(0, top + saved * r.height - window.innerHeight * 0.62), behavior: REDUCE ? 'auto' : 'smooth' }); hide();
      });
      if (x) x.addEventListener('click', hide);
      requestAnimationFrame(function () { resumeEl.classList.add('is-on'); });
    })();

    /* =========================================================
       8) BOOKMARK (save-for-later, persisted)
       ========================================================= */
    var bmBtn = $('[data-gxrd-bookmark]', root);
    if (bmBtn) {
      var SAVED = store.get('gxrd:saved', []), isSaved = SAVED.some(function (s) { return s.key === key; });
      var paintBm = function () { bmBtn.setAttribute('aria-pressed', isSaved ? 'true' : 'false'); var lab = $('[data-gxrd-bmlabel]', bmBtn); if (lab) lab.textContent = isSaved ? 'Saved to read later' : 'Save to read later'; };
      paintBm();
      bmBtn.addEventListener('click', function () {
        SAVED = store.get('gxrd:saved', []);
        if (SAVED.some(function (s) { return s.key === key; })) { SAVED = SAVED.filter(function (s) { return s.key !== key; }); isSaved = false; toast('Removed from saved'); }
        else { SAVED.push({ key: key, title: docTitle, url: location.pathname, ts: Date.now() }); isSaved = true; toast('Saved to read later'); }
        store.set('gxrd:saved', SAVED); paintBm();
      });
    }

    /* =========================================================
       9) DOCK + ACCESSIBLE DRAWERS (inert + focus trap + return)
       ========================================================= */
    var lastTrigger = null;
    function syncInert() {
      [leftRail, rightRail].forEach(function (rail) {
        if (!rail) return;
        if (isModal() && !rail.classList.contains('is-open')) { rail.setAttribute('inert', ''); rail.setAttribute('aria-hidden', 'true'); }
        else { rail.removeAttribute('inert'); rail.removeAttribute('aria-hidden'); }
      });
    }
    function openRail(which, trigger, focusSel) {
      var rail = which === 'left' ? leftRail : rightRail, other = which === 'left' ? rightRail : leftRail;
      if (other) other.classList.remove('is-open');
      if (!rail) return;
      rail.classList.add('is-open'); root.classList.add('is-drawer-open');
      syncInert();
      $all('[data-opens]', root).forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
      if (trigger) trigger.setAttribute('aria-pressed', 'true');
      if (isModal()) { var f = (focusSel && $(focusSel, rail)) || rail.querySelector('button, input, a, [tabindex]'); if (f) try { f.focus(); } catch (e) {} }
    }
    function closeDrawers() {
      var wasOpen = root.classList.contains('is-drawer-open');
      if (leftRail) leftRail.classList.remove('is-open'); if (rightRail) rightRail.classList.remove('is-open');
      root.classList.remove('is-drawer-open');
      syncInert();
      $all('[data-opens]', root).forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
      if (wasOpen && isModal() && lastTrigger) { try { lastTrigger.focus(); } catch (e) {} }
    }
    $all('[data-opens]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        var which = b.getAttribute('data-opens'), rail = which === 'left' ? leftRail : rightRail;
        if (rail && rail.classList.contains('is-open')) closeDrawers();
        else { lastTrigger = b; openRail(which, b, b.getAttribute('data-focus') || null); }
      });
    });
    var backdrop = $('.gxrd__backdrop', root); if (backdrop) backdrop.addEventListener('click', closeDrawers);
    $all('.gxrd__rail-close', root).forEach(function (b) { b.addEventListener('click', closeDrawers); });
    /* lightweight focus trap while a drawer is modal */
    on(document, 'focusin', function (e) {
      if (!root.classList.contains('is-drawer-open') || !isModal()) return;
      var openEl = (leftRail && leftRail.classList.contains('is-open')) ? leftRail : ((rightRail && rightRail.classList.contains('is-open')) ? rightRail : null);
      if (!openEl || openEl.contains(e.target)) return;
      var f = openEl.querySelector('button, input, a, [tabindex]'); if (f) try { f.focus(); } catch (e2) {}
    });
    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (root.classList.contains('is-drawer-open')) closeDrawers();
      else if (sel && sel.classList.contains('is-on')) hideSel();
      else if (resumeEl && resumeEl.classList.contains('is-on')) resumeEl.classList.remove('is-on');
    });
    var topBtn = $('[data-gxrd-top]', root);
    if (topBtn) topBtn.addEventListener('click', function () { var head = $('.gxr-article__head', root) || body; head.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' }); closeDrawers(); });
    syncInert();

    /* =========================================================
       BOOT — apply saved highlights, wire scroll, first paint
       ========================================================= */
    HLS.slice().sort(function (a, b) { return b.start - a.start; }).forEach(function (h) { applyRange(h.start, h.end, h.color, h.id); });
    renderHls();
    var onScroll = rafThrottle(function () { paintProgress(); spy(); });
    on(window, 'scroll', onScroll, { passive: true });
    on(window, 'resize', rafThrottle(function () { measureHeads(); syncInert(); paintProgress(); spy(); }));
    paintProgress(); spy(); updateStats();

    /* ---------- toast ---------- */
    var toastEl = $('.gxrd-toast', root), toastT;
    function toast(msg) { if (!toastEl) return; toastEl.textContent = msg; toastEl.classList.add('is-on'); clearTimeout(toastT); toastT = setTimeout(function () { toastEl.classList.remove('is-on'); }, 2200); }

    /* ---------- teardown (Theme Editor section reload) ---------- */
    root.__gxrdDestroy = function () { try { if (ac) ac.abort(); } catch (e) {} clearInterval(timerId); try { if (synth) synth.cancel(); } catch (e) {} };
  }

  /* ---------- run + lifecycle (registered once) ---------- */
  function boot() { $all('[data-gxrd]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { var r = e.target.querySelector ? e.target.querySelector('[data-gxrd]') : null; if (r) init(r); });
  document.addEventListener('shopify:section:unload', function (e) { var r = e.target.querySelector ? e.target.querySelector('[data-gxrd]') : null; if (r && r.__gxrdDestroy) r.__gxrdDestroy(); });
})();
